<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="bg-white rounded-lg shadow-lg p-6 sm:p-8 mb-6">
        <h1 class="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p class="text-gray-600">
          <strong>Cash Request Management System</strong>
        </p>
        <p class="text-gray-500 text-sm mt-2">Last Updated: {{ lastUpdated }}</p>
      </div>

      <!-- Content -->
      <div class="bg-white rounded-lg shadow-lg p-6 sm:p-8 space-y-8">
        <!-- Introduction -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
          <p class="text-gray-700 leading-relaxed">
            This privacy policy explains how the Cash Request Management System (the "App") handles information
            when you use our application for managing cash requests within your organization.
            Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy,
            please do not access the App.
          </p>
        </section>

        <!-- Information We Collect -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
          <p class="text-gray-700 leading-relaxed mb-3">
            We don't collect any personal data from the user. The app is intended to be a central hub for cash requests only.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">2.1 Login Credentials</h3>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Email address (provided by your organization's administrator)</li>
            <li>Password (encrypted and provided by your organization's administrator)</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">2.2 Cash Request Data</h3>
          <p class="text-gray-700 leading-relaxed">
            The App processes cash request information (purpose, amount, dates, approval status) necessary for the
            cash request workflow. This data is managed by and belongs to your organization.
          </p>
        </section>

        <!-- What We Don't Collect -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">3. What We Don't Collect</h2>
          <p class="text-gray-700 leading-relaxed mb-3">
            <strong>We do not collect, track, or store any personal data beyond what is described above.</strong> Specifically, we do not:
          </p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Track your location or device information</li>
            <li>Collect analytics or usage statistics</li>
            <li>Store personal information about employees</li>
            <li>Use cookies or tracking technologies</li>
            <li>Collect any data from your device</li>
            <li>Access your contacts, photos, or other device data</li>
          </ul>
        </section>

        <!-- How We Use Your Information -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">4. How We Use Your Information</h2>
          <p class="text-gray-700 leading-relaxed mb-3">
            The information collected is used solely for:
          </p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Authenticating users to access the App</li>
            <li>Processing cash requests within your organization's workflow</li>
            <li>Enabling the approval process for cash requests</li>
          </ul>
          <p class="text-gray-700 leading-relaxed mt-3">
            <strong>We do not use your information for any other purpose, including marketing, analytics, or third-party services.</strong>
          </p>
        </section>

        <!-- Data Sharing and Disclosure -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
          <p class="text-gray-700 leading-relaxed mb-3">
            <strong>We do not share, sell, rent, or disclose any data to third parties.</strong>
          </p>
          <p class="text-gray-700 leading-relaxed">
            All cash request data remains within your organization and is only accessible to authorized users
            (employees, approvers, administrators) within your company as necessary to process cash requests.
          </p>
        </section>

        <!-- Data Security -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
          <p class="text-gray-700 leading-relaxed mb-3">
            We implement security measures to protect your login credentials and cash request data:
          </p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Passwords are encrypted using industry-standard hashing algorithms</li>
            <li>Secure authentication system</li>
            <li>Role-based access controls to ensure only authorized users can access specific data</li>
          </ul>
        </section>

        <!-- Data Retention -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
          <p class="text-gray-700 leading-relaxed">
            Data retention is managed entirely by your organization. Cash request records are retained according to
            your organization's financial record-keeping policies and applicable legal requirements. Login credentials
            remain active as long as your organization maintains your account.
          </p>
        </section>

        <!-- Your Rights -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">8. Your Rights</h2>
          <p class="text-gray-700 leading-relaxed mb-3">
            You can review your cash request history within the App at any time. For questions about your account
            or data, please contact your organization's administrator who manages the system.
          </p>
        </section>

        <!-- Third-Party Services -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Services</h2>
          <p class="text-gray-700 leading-relaxed">
            <strong>The App does not integrate with or use any third-party services.</strong> No data is shared with,
            transmitted to, or processed by any external third-party platforms, analytics services, or advertising networks.
          </p>
        </section>

        <!-- Changes to This Privacy Policy -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
          <p class="text-gray-700 leading-relaxed">
            We may update this privacy policy from time to time. Any changes will be posted on this page with an updated
            "Last Updated" date. Your continued use of the App after any changes indicates your acceptance of the
            updated privacy policy.
          </p>
        </section>

        <!-- Contact Information -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
          <p class="text-gray-700 leading-relaxed mb-3">
            If you have questions about this privacy policy or the App, please contact your organization's administrator
            or the App support:
          </p>
          <div class="bg-blue-50 p-4 rounded-lg">
            <p class="text-gray-700">Email: <a href="mailto:info@maicoltd.com" class="text-blue-600 hover:text-blue-800">info@maicoltd.com</a></p>
            <p class="text-gray-700 mt-2 text-sm"><em>Note: For account-specific questions, please contact your organization's administrator.</em></p>
          </div>
        </section>

        <!-- Consent -->
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">12. Consent</h2>
          <p class="text-gray-700 leading-relaxed">
            By using the App, you acknowledge that you have read and understood this privacy policy. If you do not agree
            with this policy, please do not use the App.
          </p>
        </section>
      </div>

      <!-- Back to Login Button (for public access) -->
      <div class="mt-6 text-center">
        <router-link
          to="/"
          class="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Back to Login
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  data() {
    return {
      lastUpdated: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
  },
  mounted() {
    // Scroll to top when component is mounted
    window.scrollTo(0, 0);
  }
};
</script>

<style scoped>
/* Add smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Ensure proper spacing for nested lists */
ul ul {
  margin-top: 0.5rem;
  margin-left: 1.5rem;
}

/* Make links more visible */
a {
  text-decoration: underline;
}
</style>
